// Page "Uploads" : liste des uploads par statut + bouton d'upload PDF.
//
// Onglets :
//   - À revoir   : ocr_done (prêts pour la revue humaine)
//   - En cours   : pending / uploaded / ocr_queued
//   - Erreurs    : failed
//   - Validés    : discarded (déjà transformés en Invoice)

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client";
import { invoiceUploadsApi, putToS3 } from "../api/invoiceUploads";
import type { InvoiceUploadPublic, UploadStatus } from "../api/types";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";

type Tab = "to_review" | "in_progress" | "failed" | "validated";

// TAB_LABELS is now built inside the component via useTranslation

const TAB_STATUSES: Record<Tab, UploadStatus[]> = {
  to_review: ["ocr_done"],
  in_progress: ["pending", "uploaded", "ocr_queued"],
  failed: ["failed"],
  validated: ["discarded"],
};

function formatBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(2)} Mo`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UploadsPage() {
  const { t } = useTranslation();
  const TAB_LABELS: Record<Tab, string> = {
    to_review: t("uploadsPage.tabReview"),
    in_progress: t("uploadsPage.tabProcessing"),
    failed: t("uploadsPage.tabErrors"),
    validated: t("uploadsPage.tabValidated"),
  };
  const [uploads, setUploads] = useState<InvoiceUploadPublic[] | null>(null);
  const [tab, setTab] = useState<Tab>("to_review");
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState<"customer" | "supplier">("customer");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function reload() {
    setError(null);
    try {
      // On charge TOUS les uploads (limit 200) et on filtre côté client —
      // ça évite 4 appels séparés pour les 4 onglets. À 200 uploads/tenant
      // c'est OK ; on paginera si ça dépasse.
      const all = await invoiceUploadsApi.list({ limit: 200 });
      setUploads(all);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
      setUploads([]);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  // Polling automatique : tant qu'il y a des uploads "en cours", on rafraîchit
  // toutes les 3 secondes. On arrête dès qu'il n'y en a plus.
  useEffect(() => {
    const hasInProgress = uploads?.some((u) =>
      (["pending", "uploaded", "ocr_queued"] as UploadStatus[]).includes(u.status)
    );
    if (!hasInProgress) return;

    const timer = setInterval(() => {
      void reload();
    }, 3000);
    return () => clearInterval(timer);
  }, [uploads]);

  const filtered = useMemo(() => {
    if (!uploads) return [];
    const wanted = new Set(TAB_STATUSES[tab]);
    return uploads.filter((u) => wanted.has(u.status));
  }, [uploads, tab]);

  const counts = useMemo(() => {
    const out: Record<Tab, number> = {
      to_review: 0,
      in_progress: 0,
      failed: 0,
      validated: 0,
    };
    if (!uploads) return out;
    for (const t of Object.keys(TAB_STATUSES) as Tab[]) {
      const wanted = new Set(TAB_STATUSES[t]);
      out[t] = uploads.filter((u) => wanted.has(u.status)).length;
    }
    return out;
  }, [uploads]);

  async function onFileSelected(file: File) {
    setUploading(true);
    setUploadError(null);
    try {
      if (file.type !== "application/pdf") {
        throw new Error("Seuls les PDF sont acceptés pour l'instant");
      }
      await invoiceUploadsApi.directUpload(file, uploadType);
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Upload impossible";
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("uploadsPage.title")}</h1>
          <p className="text-sm text-slate-500">
            {t("uploadsPage.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600">Type :</span>
            <select value={uploadType} onChange={e => setUploadType(e.target.value as "customer" | "supplier")}
              className="text-sm rounded-md border border-slate-200 px-2 py-1.5">
              <option value="customer">📥 Client (revenu)</option>
              <option value="supplier">📤 Fournisseur (dépense)</option>
            </select>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="sr-only"
            id="upload-pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFileSelected(f);
            }}
          />
          <label
            htmlFor="upload-pdf"
            className={[
              "btn-primary cursor-pointer inline-block",
              uploading ? "opacity-50 pointer-events-none" : "",
            ].join(" ")}
            aria-disabled={uploading}
          >
            {uploading ? t("uploadsPage.processing") : `+ ${t("uploadsPage.upload")}`}
          </label>
        </div>
      </header>

      {uploadError && (
        <p
          role="alert"
          className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
        >
          {uploadError}
        </p>
      )}

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={[
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
              tab === t
                ? "border-brand-500 text-brand-600"
                : "border-transparent text-slate-500 hover:text-slate-700",
            ].join(" ")}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
            <span className="ml-1.5 text-xs text-slate-400">
              {counts[t]}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {error && (
          <p role="alert" className="p-4 text-sm text-red-600">
            {error}
          </p>
        )}
        {uploads === null && (
          <p className="p-6 text-sm text-slate-500">{t("common.loading")}</p>
        )}
        {uploads !== null && filtered.length === 0 && !error && (
          <p className="p-6 text-sm text-slate-500">{t("uploadsPage.noUploads")}</p>
        )}
        {filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("uploadsPage.colFile")}</th>
                <th className="px-4 py-2 font-medium">{t("uploadsPage.colSize")}</th>
                <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                <th className="px-4 py-2 font-medium">{t("uploadsPage.colDate")}</th>
                <th className="px-4 py-2 font-medium w-36 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-900">
                    {u.original_filename}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatBytes(u.size_bytes)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{u.status}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatDate(u.created_at)}
                  </td>
                  <td className="px-4 py-2 text-right space-x-3">
                    {u.status === "ocr_done" && (
                      <Link
                        to={`/uploads/${u.id}/review`}
                        className="text-brand-600 hover:underline"
                      >
                        {t("uploadsPage.tabReview")}
                      </Link>
                    )}
                    {u.status === "failed" && (
                      <button
                        type="button"
                        className="text-brand-600 hover:underline"
                        onClick={async () => {
                          try {
                            await invoiceUploadsApi.reOcr(u.id);
                            await reload();
                          } catch (err) {
                            const msg =
                              err instanceof ApiError ? err.detail : "Relance impossible";
                            window.alert(msg);
                          }
                        }}
                      >
                        {t("uploadsPage.processing")}
                      </button>
                    )}
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 hover:underline"
                      onClick={async () => {
                        if (!window.confirm(`Supprimer ${u.original_filename} ?`)) return;
                        try {
                          await invoiceUploadsApi.delete(u.id);
                          await reload();
                        } catch (err) {
                          const msg =
                            err instanceof ApiError ? err.detail : "Suppression impossible";
                          window.alert(msg);
                        }
                      }}
                    >
                      {t("common.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
